import {Moment} from 'moment-timezone'

import {SLIDER_ROUND_TO_INTERVAL_HOURS} from '../constants'
import type {MomentRange, NumberRange} from '../types'

export const toPixel = (value: number): string => `${value}px`

export const calculateRangeWithinBoundary = (
  inputRange: [number, number],
  boundary: [number, number]
): [number, number] => {
  const [startRange, endRange] = inputRange
  const delta = endRange - startRange
  const [min, max] = boundary

  if (delta > max - min) {
    return boundary
  }
  if (startRange < min) {
    return [min, min + delta]
  }
  if (endRange > max) {
    return [max - delta, max]
  }
  return inputRange
}

export const calculateDatetimeRangeWithinBoundary = (
  inputRange: MomentRange,
  boundary: MomentRange
): MomentRange => {
  const [startRange, endRange] = inputRange
  const delta = endRange.diff(startRange, 'hours')
  const [min, max] = boundary

  if (delta > max.diff(min, 'hours')) {
    return boundary
  }
  if (startRange.isBefore(min)) {
    return [min, min.clone().add(delta, 'hours')]
  }
  if (endRange.isAfter(max)) {
    return [max.clone().subtract(delta, 'hours'), max]
  }
  return inputRange
}

const adjustRangeByMinLimit = (range: MomentRange, minHours: number): MomentRange => {
  const [start, end] = range
  const rangeLenghtInHours = end.diff(start, 'hours')
  return rangeLenghtInHours < minHours ? [start, start.clone().add(minHours, 'hours')] : range
}

export const adjustRangeByMinMaxLimitAndBoundary = (
  range: MomentRange,
  boundary: MomentRange,
  minMaxRangeHours: NumberRange
) => {
  const [minRangeHours, maxRangeHours] = minMaxRangeHours
  const rangeAdjustedByMinLimit = adjustRangeByMinLimit(range, minRangeHours)
  const rangeAdjustedByMaxLimit = calculateDatetimeRangeWithinBoundary(rangeAdjustedByMinLimit, [
    rangeAdjustedByMinLimit[0],
    rangeAdjustedByMinLimit[0].clone().add(maxRangeHours, 'hours')
  ])
  return calculateDatetimeRangeWithinBoundary(rangeAdjustedByMaxLimit, boundary)
}

export function roundTimeDownToNearest12Hours(date: Moment) {
  const hours = date.hours()
  const roundedHours =
    Math.floor(hours / SLIDER_ROUND_TO_INTERVAL_HOURS) * SLIDER_ROUND_TO_INTERVAL_HOURS
  return date.clone().startOf('day').add(roundedHours, 'hours')
}

export function roundTimeUpToNearest12Hours(date: Moment) {
  const hours = date.hours()
  const roundedHours =
    Math.ceil(hours / SLIDER_ROUND_TO_INTERVAL_HOURS) * SLIDER_ROUND_TO_INTERVAL_HOURS
  return date.clone().startOf('day').add(roundedHours, 'hours')
}
